import dayjs from 'dayjs';
import { ISessionManagerExternalProvider } from 'interfaces/providers/ISessionManagerExternalProvider';
import { ISessionManagerProvider } from 'interfaces/providers/ISessionManagerProvider';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { ReviewDataRow } from 'models/review/ReviewDataRow/ReviewDataRow';
import { ReviewGame } from 'models/review/ReviewGame';
import { ContextualService } from 'services/data-services/ContextualService';
import { GameReviewService } from 'services/data-services/GameReviewService';
import { mergePlaysWithEmptyPlaysAndFixComputedProperties } from 'services/helper-services/playManipulationHelpers';
import { ITaggingProvider } from '../interfaces/providers/ITaggingProvider';
import { LogService } from '../services/data-services/LogService';
import { sleep } from '../services/helper-services/utils';
import { BaseStore } from './BaseStore';
import { NotificationStore } from './NotificationStore';

export class GameReviewStore extends BaseStore {
  constructor(
    gameReviewService: GameReviewService,
    taggingService: ITaggingProvider,
    sessionManager: ISessionManagerProvider,
    sessionManagerExternal: ISessionManagerExternalProvider,
    logService: LogService,
    contextualService: ContextualService,
    notificationStore: NotificationStore
  ) {
    super(logService);
    this.gameReviewService = gameReviewService;
    this.taggingService = taggingService;
    this.sessionManager = sessionManager;
    this.sessionManagerExternal = sessionManagerExternal;
    this.contextualService = contextualService;
    this.notificationStore = notificationStore;
    makeObservable(this);
  }

  private contextualService: ContextualService;

  private gameReviewService: GameReviewService;

  private taggingService: ITaggingProvider;

  private sessionManager: ISessionManagerProvider;

  private sessionManagerExternal: ISessionManagerExternalProvider;

  private notificationStore: NotificationStore;

  @observable isReviewing = false;

  @observable reviewGame: ReviewGame = { sessionMetadata: {} } as ReviewGame;

  @observable isSaving = false;

  @observable isSubmitModalVisible = false;

  @action setIsReviewing = (isReviewing: boolean) => {
    this.isReviewing = isReviewing;
  };

  @action setIsSubmitModalVisible = (visible: boolean) => {
    this.isSubmitModalVisible = visible;
  };

  @action setReviewGame = async (sessionId: string) => {
    try {
      this.isLoading = true;
      const game = new ReviewGame();
      const playsPromise = this.gameReviewService.getReviewPlays(sessionId);
      const sessionMetadata = await this.sessionManagerExternal.getSessionMetadata(sessionId);
      const sessionRosters = await this.sessionManager.getSessionRoster(sessionId);
      const plays = await playsPromise;
      game.sessionId = sessionId;
      if (sessionRosters) {
        game.homeTeamPlayers = sessionRosters.homeTeamRoster;
        game.awayTeamPlayers = sessionRosters.awayTeamRoster;
      }
      game.sessionMetadata = sessionMetadata;
      game.plays = mergePlaysWithEmptyPlaysAndFixComputedProperties(plays.plays, plays.missingPlays, sessionMetadata);
      console.log(game);
      if (game !== null) {
        this.reviewGame = {
          ...game,
          field: game.sessionMetadata.location.field.name,
          taggedOn: dayjs(game.sessionMetadata.sessionModeState.sessionStartedLocal).format('YYYY-MM-DD HH:mm'),
          gameReference: game.sessionMetadata.gameReference,
        };
      }
      if (game.plays.length === 0) {
        this.notificationStore.addNotification({
          id: 'no-plays-for-review-game',
          message: `No plays for session with id: ${game.sessionId}`,
          variant: 'error',
          removable: true,
          onClose: () => {
            this.notificationStore.removeNotification('no-plays-for-review-game');
          },
        });
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        this.notificationStore.addNotification({
          id: 'error-loading-review-game',
          message: err.message,
          variant: 'error',
          removable: true,
          onClose: () => {
            this.notificationStore.removeNotification('error-loading-review-game');
          },
        });
      }
    } finally {
      this.isLoading = false;
    }
  };

  @action loadRosters = async (homeTeamId: string, awayTeamId: string) => {
    try {
      const homeTask = this.contextualService.GetTeamRoster(homeTeamId);
      const awayTask = this.contextualService.GetTeamRoster(awayTeamId);
      const [home, away] = await Promise.all([homeTask, awayTask]);
      runInAction(() => {
        this.reviewGame.homeTeamPlayers = home.players;
        this.reviewGame.awayTeamPlayers = away.players;
      });
    } catch (err: unknown) {
      if (err instanceof Error) {
        this.notificationStore.addNotification({
          id: 'error-loading-team-rosters',
          message: err.message,
          variant: 'error',
          removable: true,
          onClose: () => {
            this.notificationStore.removeNotification('error-loading-team-rosters');
          },
        });
      }
    }
  };

  @action savePlays = async (rows: ReviewDataRow[]) => {
    const result = { changedIds: [] as string[], isSuccessful: false };
    if (!this.reviewGame) {
      return result;
    }
    try {
      this.isSaving = true;
      this.reviewGame.plays = rows.map((r) => r.toApiPlay(this.reviewGame.sessionMetadata));
      const playsToUpdate = this.reviewGame.plays.filter((p) => p.isChanged);
      result.changedIds = playsToUpdate.map((p) => p.play.id);
      await this.taggingService.storePlays(
        playsToUpdate.map((p) => p.play),
        'GameReviewStore@savePlays'
      );
      result.isSuccessful = true;
    } catch (error: unknown) {
      if (error instanceof Error) {
        this.notificationStore.addNotification({
          id: 'error-saving-plays',
          message: error.message,
          variant: 'error',
          removable: true,
          onClose: () => {
            this.notificationStore.removeNotification('error-saving-plays');
          },
        });
      }
      result.isSuccessful = false;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
    return result;
  };

  @action submitReviewGame = async (sessionId: string) => {
    try {
      this.isLoading = true;
      await this.sessionManager.submitSessionMetadata(sessionId);
      await sleep(500);
    } catch (err: any) {
      if (err instanceof Error) {
        this.notificationStore.addNotification({
          id: 'error-submitting-review-game',
          message: err.message,
          variant: 'error',
          removable: true,
          onClose: () => {
            this.notificationStore.removeNotification('error-submitting-review-game');
          },
        });
      }
    } finally {
      this.isLoading = false;
    }
  };
}
